"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

interface RumPageload {
  type: "pageload";
  path: string;
  ttfb: number;
  fcp?: number;
  lcp?: number;
  loadMs?: number;
  cls?: number;
}

interface RumError {
  type: "error";
  path: string;
  errorType: string;
  errorMsg: string;
}

type RumItem = RumPageload | RumError;

const ENDPOINT = "/api/rum";

function send(item: RumItem) {
  try {
    const blob = new Blob([JSON.stringify(item)], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
  } catch {
    // fall through to fetch
  }
  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(item),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Lightweight client-side Real User Monitoring tracker.
 * Captures Navigation Timing + Largest Contentful Paint + Cumulative Layout Shift
 * and reports them once per page load. Also forwards JS errors / unhandled rejections.
 */
export default function RumTracker() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip admin/platforma authoring routes — not useful as user-facing pageloads
    if (pathname?.startsWith("/api")) return;

    let lcp = 0;
    let cls = 0;
    let fcp = 0;
    let sent = false;

    // ── LCP ─────────────────────────────────────────────────
    let lcpObserver: PerformanceObserver | undefined;
    try {
      lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const last = entries[entries.length - 1];
        if (last) lcp = Math.round((last as PerformanceEntry & { renderTime?: number; loadTime?: number }).startTime || 0);
      });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
    } catch {
      /* unsupported */
    }

    // ── CLS ─────────────────────────────────────────────────
    let clsObserver: PerformanceObserver | undefined;
    try {
      clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const e = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
          if (!e.hadRecentInput && typeof e.value === "number") cls += e.value;
        }
      });
      clsObserver.observe({ type: "layout-shift", buffered: true });
    } catch {
      /* unsupported */
    }

    // ── FCP ─────────────────────────────────────────────────
    try {
      const paintObserver = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          if (e.name === "first-contentful-paint") fcp = Math.round(e.startTime);
        }
      });
      paintObserver.observe({ type: "paint", buffered: true });
    } catch {
      /* unsupported */
    }

    // Send once page is ready
    const flush = () => {
      if (sent) return;
      sent = true;
      try {
        lcpObserver?.disconnect();
        clsObserver?.disconnect();
      } catch {
        /* noop */
      }
      const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
      const ttfb = nav ? Math.round(nav.responseStart) : 0;
      const loadMs = nav ? Math.round(nav.loadEventEnd || nav.domContentLoadedEventEnd) : 0;
      send({
        type: "pageload",
        path: pathname || window.location.pathname,
        ttfb,
        fcp: fcp || undefined,
        lcp: lcp || undefined,
        loadMs: loadMs || undefined,
        cls: Math.round(cls * 1000) / 1000,
      });
    };

    // Flush after `load` + small delay so LCP can settle
    let flushTimer: number | null = null;
    const onLoad = () => {
      flushTimer = window.setTimeout(flush, 1500);
    };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });

    // Also flush when user leaves the page
    const onHide = () => flush();
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHide();
    });
    window.addEventListener("pagehide", onHide);

    // ── Error tracking ──────────────────────────────────────
    const onError = (e: ErrorEvent) => {
      send({
        type: "error",
        path: pathname || window.location.pathname,
        errorType: "js",
        errorMsg: (e.message || "Unknown error").slice(0, 500),
      });
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const msg =
        typeof reason === "string"
          ? reason
          : reason && typeof reason === "object" && "message" in reason
            ? String((reason as { message?: unknown }).message)
            : "Unhandled promise rejection";
      send({
        type: "error",
        path: pathname || window.location.pathname,
        errorType: "promise",
        errorMsg: msg.slice(0, 500),
      });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    return () => {
      if (flushTimer) window.clearTimeout(flushTimer);
      window.removeEventListener("load", onLoad);
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      try {
        lcpObserver?.disconnect();
        clsObserver?.disconnect();
      } catch {
        /* noop */
      }
    };
  }, [pathname]);

  return null;
}
